import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, KeyRound, Plus, Edit3, Trash2, Download, ShieldCheck, EyeOff, Eye } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { VAULT } from '@/constants/testIds';
import {
	deriveKey,
	encryptString,
	decryptString,
	getOrCreateSaltForUser,
	buildRecoveryPackage,
} from '@/lib/crypto';

export default function Vault() {
	const { user } = useAuth();
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [passphrase, setPassphrase] = useState('');
	const [showPp, setShowPp] = useState(false);
	const [key, setKey] = useState(null);
	const [decrypted, setDecrypted] = useState({}); // {item_id: {label, body}}
	const [editing, setEditing] = useState(null); // item or "new"
	const [form, setForm] = useState({ category: 'Note', label: '', body: '' });
	const isPremium = user?.plan === 'PREMIUM' || user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';

	const salt = user ? getOrCreateSaltForUser(user.user_id) : null;

	useEffect(() => {
		if (!isPremium) {
			setLoading(false);
			return;
		}
		(async () => {
			try {
				const { data } = await api.get('/vault');
				setItems(data.items || []);
			} catch (err) {
				toast.error(formatApiError(err));
			} finally {
				setLoading(false);
			}
		})();
	}, [isPremium]);

	const unlock = async (e) => {
		e?.preventDefault?.();
		if (!passphrase) {
			toast.error('Enter your vault passphrase');
			return;
		}
		try {
			const k = await deriveKey(passphrase, salt);
			setKey(k);
			// try to decrypt each item
			const next = {};
			for (const it of items) {
				try {
					const label = await decryptString(k, it.iv, it.label_cipher);
					let body = '';
					if (it.body_cipher?.includes(':')) {
						const [biv, bct] = it.body_cipher.split(':');
						body = await decryptString(k, biv, bct);
					} else {
						body = await decryptString(k, it.iv, it.body_cipher);
					}
					next[it.item_id] = { label, body };
				} catch {
					// Wrong key or item from a different passphrase — skip.
				}
			}
			setDecrypted(next);
			setPassphrase('');
			toast.success('Vault unlocked');
		} catch (err) {
			toast.error('Could not unlock vault');
		}
	};

	const lockVault = () => {
		setKey(null);
		setDecrypted({});
	};

	const openCreate = () => {
		if (!key) {
			toast.error('Unlock the vault first');
			return;
		}
		setEditing('new');
		setForm({ category: 'Note', label: '', body: '' });
	};

	const openEdit = (it) => {
		if (!key) return;
		const d = decrypted[it.item_id];
		if (!d) {
			toast.error('Could not decrypt this item with the current passphrase.');
			return;
		}
		setEditing(it);
		setForm({ category: it.category, label: d.label, body: d.body });
	};

	const save = async (e) => {
		e?.preventDefault?.();
		if (!key) return;
		try {
			const { iv: labelIv, cipher: labelCipher } = await encryptString(key, form.label);
			// Encrypt body with the SAME IV is unsafe (IV reuse). We generate a single
			// IV per item but apply it via encryptString twice, each call generates its
			// own IV. We store both IVs by keeping one IV per item: use label IV for the
			// item record and combine body with a fresh IV inside the cipher payload.
			// To keep schema simple we generate two ciphers but only store the label IV
			// and embed the body IV inside the body cipher as `iv:cipher`.
			const { iv: bodyIv, cipher: bodyCipher } = await encryptString(key, form.body);
			const body_cipher = `${bodyIv}:${bodyCipher}`;
			const payload = {
				category: form.category || 'Note',
				label_cipher: labelCipher,
				body_cipher,
				iv: labelIv,
				salt,
			};
			if (editing && editing !== 'new') {
				await api.put(`/vault/${editing.item_id}`, payload);
			} else {
				await api.post('/vault', payload);
			}
			const { data } = await api.get('/vault');
			setItems(data.items || []);
			// Refresh decrypted cache for this entry
			setDecrypted((d) => ({ ...d, [(editing === 'new' ? null : editing.item_id) || 'temp']: { label: form.label, body: form.body } }));
			// Re-decrypt all so list shows fresh labels
			const next = {};
			for (const it of (data.items || [])) {
				try {
					const label = await decryptString(key, it.iv, it.label_cipher);
					let body = '';
					if (it.body_cipher?.includes(':')) {
						const [biv, bct] = it.body_cipher.split(':');
						body = await decryptString(key, biv, bct);
					} else {
						body = await decryptString(key, it.iv, it.body_cipher);
					}
					next[it.item_id] = { label, body };
				} catch {
					/* skip */
				}
			}
			setDecrypted(next);
			setEditing(null);
			toast.success('Saved securely');
		} catch (err) {
			toast.error(formatApiError(err, 'Could not save'));
		}
	};

	const remove = async (it) => {
		if (!window.confirm('Delete this encrypted item?')) return;
		try {
			await api.delete(`/vault/${it.item_id}`);
			setItems((arr) => arr.filter((x) => x.item_id !== it.item_id));
			toast.success('Removed');
		} catch (err) {
			toast.error(formatApiError(err));
		}
	};

	const downloadRecovery = () => {
		if (!user) return;
		const pkg = buildRecoveryPackage(user.user_id, salt);
		const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `myfamshield-recovery-${user.user_id}.json`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		setTimeout(() => URL.revokeObjectURL(url), 1000);
		toast.success('Recovery package downloaded. Keep it safe and offline.');
	};

	if (!isPremium) {
		return (
			<Layout>
				<div data-testid={VAULT.root} className="card-soft p-10 text-center max-w-2xl mx-auto">
					<div className="mx-auto w-16 h-16 rounded-3xl bg-emerald-100 text-emerald-700 grid place-items-center">
						<Lock className="w-7 h-7" />
					</div>
					<h2 className="mt-4 font-heading text-2xl font-semibold text-slate-900">Private Family Vault</h2>
					<p className="mt-2 text-slate-600">A premium space for encrypted family notes, blueprints and legacy messages — locked with a passphrase only you know.</p>
					<Link to="/subscription"><Button className="mt-6 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF]">Upgrade to Premium</Button></Link>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div data-testid={VAULT.root} className="space-y-6">
				<div className="flex items-center justify-between flex-wrap gap-3">
					<div>
						<div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Premium</div>
						<h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight text-slate-900 flex items-center gap-3">
							Private Family Vault <ShieldCheck className="w-7 h-7 text-emerald-600" />
						</h1>
						<p className="mt-2 text-slate-600 max-w-2xl">
							Notes, blueprints, instructions and legacy messages — encrypted on your device. Our servers only ever see encrypted blobs.
						</p>
					</div>
					{key ? (
						<div className="flex items-center gap-2">
							<Button variant="outline" className="rounded-full" onClick={downloadRecovery}>
								<Download className="w-4 h-4 mr-2" /> Recovery package
							</Button>
							<Button variant="outline" className="rounded-full" onClick={lockVault}>
								<Lock className="w-4 h-4 mr-2" /> Lock vault
							</Button>
							<Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={openCreate} data-testid={VAULT.createButton}>
								<Plus className="w-4 h-4 mr-2" /> New item
							</Button>
						</div>
					) : null}
				</div>

				{!key && (
					<form onSubmit={unlock} className="glass-vault p-6 sm:p-8 max-w-xl">
						<div className="text-xs uppercase tracking-[0.2em] font-bold text-blue-800 flex items-center gap-2">
							<KeyRound className="w-4 h-4" /> Unlock your vault
						</div>
						<p className="mt-2 text-slate-600 text-sm">Enter the passphrase you use for the vault. We don&apos;t know it and we can&apos;t reset it — that&apos;s the point.</p>
						<div className="mt-5">
							<Label htmlFor="pp">Vault passphrase</Label>
							<div className="relative mt-1.5">
								<Input id="pp" type={showPp ? 'text' : 'password'} value={passphrase} onChange={(e) => setPassphrase(e.target.value)} className="h-12 rounded-xl pr-10" data-testid={VAULT.passphraseInput} />
								<button type="button" onClick={() => setShowPp((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" aria-label="Toggle">
									{showPp ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
								</button>
							</div>
						</div>
						<Button type="submit" className="mt-5 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid={VAULT.unlockButton}>
							<Lock className="w-4 h-4 mr-2" /> Unlock
						</Button>
						<p className="mt-5 text-xs text-slate-500 leading-relaxed">
							⚠️ If you lose both your recovery key and your passphrase, your encrypted information cannot be recovered.
						</p>
					</form>
				)}

				{key && (
					loading ? (
						<div className="card-soft p-10 text-center text-slate-500">Loading…</div>
					) : items.length === 0 ? (
						<div className="card-soft p-10 text-center">
							<div className="text-slate-600">Your vault is empty. Start with a family blueprint or a legacy message.</div>
							<Button className="mt-4 rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={openCreate}>
								<Plus className="w-4 h-4 mr-2" /> Create your first item
							</Button>
						</div>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
							{items.map((it) => {
								const d = decrypted[it.item_id];
								return (
									<div key={it.item_id} className="glass-vault p-6">
										<div className="text-xs uppercase tracking-[0.2em] font-bold text-blue-800">{it.category}</div>
										<div className="mt-2 font-heading text-lg font-semibold text-slate-900">{d?.label || '🔒 Encrypted item'}</div>
										<div className="mt-2 text-sm text-slate-700 whitespace-pre-line line-clamp-4 min-h-[3rem]">
											{d?.body || (key ? 'Could not decrypt with current passphrase.' : '')}
										</div>
										<div className="mt-4 flex gap-2">
											<Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(it)}>
												<Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit
											</Button>
											<Button size="sm" variant="outline" className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => remove(it)}>
												<Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
											</Button>
										</div>
									</div>
								);
							})}
						</div>
					)
				)}

				<Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
					<DialogContent className="rounded-3xl">
						<DialogHeader>
							<DialogTitle>{editing === 'new' ? 'New vault item' : 'Edit vault item'}</DialogTitle>
						</DialogHeader>
						<form onSubmit={save} className="space-y-4">
							<div>
								<Label>Category</Label>
								<Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-11 rounded-xl mt-1.5" data-testid={VAULT.categoryInput} />
							</div>
							<div>
								<Label>Label</Label>
								<Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="h-11 rounded-xl mt-1.5" placeholder="e.g. Family Blueprint" data-testid={VAULT.itemLabelInput} />
							</div>
							<div>
								<Label>Encrypted note</Label>
								<Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-xl mt-1.5 min-h-[160px]" placeholder="Write your private note. This will be encrypted on your device before leaving the browser." data-testid={VAULT.itemBodyInput} />
							</div>
							<DialogFooter>
								<Button type="button" variant="outline" className="rounded-full" onClick={() => setEditing(null)}>Cancel</Button>
								<Button type="submit" className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid={VAULT.saveButton}>Save securely</Button>
							</DialogFooter>
						</form>
					</DialogContent>
				</Dialog>
			</div>
		</Layout>
	);
}
